import React, { useState } from 'react';
import { Bell, HelpCircle, Menu, Search, User, Settings, LogOut } from 'lucide-react';
import { START_TOUR_EVENT } from '../../tutorials/ProductTour';
import { tourFor } from '../../tutorials/tours';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useRouteAccess } from '../../hooks/useRouteAccess';
import { meService, notificationService } from '../../services/api';
import { useNotificationSSE } from '../../hooks/useNotificationSSE';
import { isPlatformOnlySuperAdmin } from '../../utils/platformRoles';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';
import { TenantLogo } from '../Brand/TenantBrandMark';
import { tenantBrand } from '../../utils/tenantBranding';

function formatShort(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '';
  }
}

const Header = ({ onMenu }) => {
  const { t } = useTranslation(['nav', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const { canAccessRoute } = useRouteAccess();
  const platformOnly = isPlatformOnlySuperAdmin(user);
  const hasTour = Boolean(tourFor(user, tenant));
  const canAccessNotifications = !platformOnly && canAccessRoute('/notifications');

  useNotificationSSE(!!user && canAccessNotifications);

  const { data: meServidor } = useQuery(
    ['me-servidor'],
    () => meService.getServidor().then((r) => r.data),
    { enabled: canAccessNotifications && !!user, retry: 1 }
  );
  const myServidorId = meServidor?.servidor_id || null;
  const assigneeParams = onlyMine && myServidorId ? { assignee_servidor_id: myServidorId } : {};

  const { data: unreadPayload } = useQuery(
    ['notifications-unread-count', 'org'],
    () => notificationService.getInAppUnreadCount().then((r) => r.data),
    {
      refetchInterval: 300_000,
      refetchOnWindowFocus: true,
      retry: 1,
      enabled: canAccessNotifications,
    }
  );

  const { data: mineUnreadPayload } = useQuery(
    ['notifications-unread-count', 'mine', myServidorId],
    () =>
      notificationService
        .getInAppUnreadCount({ assignee_servidor_id: myServidorId })
        .then((r) => r.data),
    {
      refetchInterval: 300_000,
      refetchOnWindowFocus: true,
      retry: 1,
      enabled: canAccessNotifications && !!myServidorId,
    }
  );

  const { data: listPayload } = useQuery(
    ['notifications-header-dropdown', onlyMine ? myServidorId : 'org'],
    () => notificationService.listInApp({ limit: 8, skip: 0, ...assigneeParams }).then((r) => r.data),
    {
      refetchInterval: 300_000,
      refetchOnWindowFocus: true,
      retry: 1,
      enabled: canAccessNotifications && showNotifications && (!onlyMine || !!myServidorId),
    }
  );

  const orgUnread = unreadPayload?.unread_count ?? 0;
  const mineUnread = mineUnreadPayload?.unread_count ?? 0;
  const unreadCount = onlyMine ? mineUnread : orgUnread;
  const notifications = Array.isArray(listPayload?.data) ? listPayload.data : [];

  const markReadMutation = useMutation(
    (id) => notificationService.markInAppRead(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notifications-unread-count']);
        queryClient.invalidateQueries(['notifications-header-dropdown']);
        queryClient.invalidateQueries(['notifications-in-app']);
        queryClient.invalidateQueries(['notifications-in-app-preview']);
      },
    }
  );

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const brand = tenantBrand(tenant);

  return (
    <header className="border-b border-gray-200 bg-white shadow-soft">
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 sm:gap-4 sm:px-6 sm:py-3.5">
        <button
          type="button"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 lg:hidden"
          aria-label="Abrir menu"
          onClick={onMenu}
        >
          <Menu className="h-5 w-5" aria-hidden />
        </button>
        {brand.logoUrl ? (
          <div className="hidden min-w-0 shrink items-center md:flex">
            <TenantLogo tenant={tenant} className="h-12 max-w-[240px] lg:h-16 lg:max-w-[420px]" />
          </div>
        ) : null}
        <div className="hidden min-w-0 flex-1 items-center gap-4 md:flex md:max-w-lg">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="search"
              placeholder={t('searchSystem')}
              aria-label={t('searchAria')}
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            />
          </div>
        </div>

        <div className="ml-auto flex items-center gap-1 sm:gap-4">
          {canAccessNotifications && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label={t('notifications')}
              aria-expanded={showNotifications}
            >
              <Bell className="h-5 w-5" aria-hidden />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-danger-500 text-white text-xs rounded-full min-w-[1.25rem] h-5 px-1 flex items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 z-50 mt-2 w-[min(20rem,calc(100vw-1.5rem))] rounded-lg border border-gray-200 bg-white shadow-strong">
                <div className="p-4 border-b border-gray-200 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-gray-900">{t('notifications')}</h3>
                    {canAccessNotifications && (
                      <Link
                        to="/notifications"
                        className="text-xs font-medium text-primary-700 hover:underline"
                        onClick={() => setShowNotifications(false)}
                      >
                        {t('notificationsHub')}
                      </Link>
                    )}
                  </div>
                  {myServidorId ? (
                    <label className="flex items-center justify-between gap-2 text-xs text-gray-600">
                      <span>
                        {t('mineCount', { count: mineUnread })} · {t('orgCount', { count: orgUnread })}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={onlyMine}
                          onChange={(e) => setOnlyMine(e.target.checked)}
                        />
                        {t('onlyMine')}
                      </span>
                    </label>
                  ) : null}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500">{t('noRecentAlerts')}</p>
                  ) : (
                    notifications.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        className="w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50"
                        onClick={() => {
                          if (!n.read_at) markReadMutation.mutate(n.id);
                          if (n.link_href) navigate(n.link_href);
                          setShowNotifications(false);
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-2 h-2 rounded-full mt-2 shrink-0 ${
                              n.severity === 'danger'
                                ? 'bg-danger-500'
                                : n.severity === 'warning'
                                  ? 'bg-warning-500'
                                  : n.severity === 'success'
                                    ? 'bg-success-500'
                                    : 'bg-info-500'
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 text-sm line-clamp-2">{n.title}</h4>
                            <p className="text-gray-600 text-sm mt-1 line-clamp-2">{n.body}</p>
                            <p className="text-gray-400 text-xs mt-1">{formatShort(n.created_at)}</p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
                {canAccessNotifications && (
                  <div className="p-4 border-t border-gray-200">
                    <Link
                      to="/notifications"
                      className="text-primary-600 text-sm font-medium hover:text-primary-700"
                      onClick={() => setShowNotifications(false)}
                    >
                      {t('viewAllNotifications')}
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          <div className="relative">
            <button
              type="button"
              data-tour="account-menu"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label={t('accountMenu')}
              aria-expanded={showUserMenu}
              aria-haspopup="true"
            >
              <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                {getUserInitials(user?.name || user?.username)}
              </div>
              <div className="text-left hidden sm:block">
                <div className="text-sm font-medium">{user?.name || user?.username}</div>
                <div className="text-xs text-gray-500">{user?.email}</div>
              </div>
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-strong border border-gray-200 z-50">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                      {getUserInitials(user?.name || user?.username)}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{user?.name || user?.username}</div>
                      <div className="text-sm text-gray-500">{user?.email}</div>
                    </div>
                  </div>
                </div>
                <div className="py-2">
                  <div className="px-4 py-2">
                    <LanguageSwitcher variant="inline" />
                  </div>
                  {hasTour ? (
                    <button
                      type="button"
                      onClick={() => {
                        setShowUserMenu(false);
                        window.dispatchEvent(new CustomEvent(START_TOUR_EVENT));
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <HelpCircle className="w-4 h-4" />
                      Ver tutorial
                    </button>
                  ) : null}
                  {canAccessRoute('/profile') && (
                    <Link
                      to="/profile"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <User className="w-4 h-4" />
                      {t('profile')}
                    </Link>
                  )}
                  {canAccessRoute('/settings') && (
                    <Link
                      to="/settings"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Settings className="w-4 h-4" />
                      {t('settings')}
                    </Link>
                  )}
                  {(canAccessRoute('/profile') || canAccessRoute('/settings')) && <hr className="my-2" />}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-danger-600 hover:bg-danger-50"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {(showUserMenu || showNotifications) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowUserMenu(false);
            setShowNotifications(false);
          }}
        />
      )}
    </header>
  );
};

export default Header;
